import {AsyncPipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatGoogleMapsAutocompleteModule} from '@angular-material-extensions/google-maps-autocomplete';
import {forkJoin, Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {LoadingService} from '@ui-kit/framework/util/service/loading.service';
import {SnackbarService} from '@ui-kit/framework/util/service/snackbar.service';
import {AddressService} from '@ui-kit/framework/util/service/address.service';
import {toAdminAddress} from '../../admin-address.util';
import {SetupService} from '../setup.service';
import PlaceResult = google.maps.places.PlaceResult;

@Component({
  selector: 'app-create-location-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatGoogleMapsAutocompleteModule,
  ],
  templateUrl: './create-location-dialog.component.html',
})
export class CreateLocationDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<CreateLocationDialogComponent>);
  private readonly data = inject<{ landlordUuid: string }>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly setupService = inject(SetupService);
  private readonly addressService = inject(AddressService);
  private readonly snackbar = inject(SnackbarService);
  protected readonly loader = inject(LoadingService);

  protected form = this.fb.group({
    name: ['', Validators.required],
    code: ['', Validators.required],
    address: ['', Validators.required],
  });

  private place: PlaceResult | null = null;
  private geometry: any = null;

  protected tileImageFile: File | null = null;
  protected bannerImageFile: File | null = null;

  generateCode(): void {
    const name = this.form.controls.name.value ?? '';
    this.form.controls.code.setValue(
      name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    );
  }

  onTileImageSelected(event: Event): void {
    this.tileImageFile = (event.target as HTMLInputElement).files?.[0] ?? null;
  }

  onBannerImageSelected(event: Event): void {
    this.bannerImageFile = (event.target as HTMLInputElement).files?.[0] ?? null;
  }

  clearTileImage(): void {
    this.tileImageFile = null;
  }

  clearBannerImage(): void {
    this.bannerImageFile = null;
  }

  onAutocompleteSelected(place: PlaceResult): void {
    this.place = place;
  }

  onLocationSelected(location: any): void {
    this.geometry = location;
  }

  onSubmit(): void {
    if (this.form.invalid || !this.place || !this.geometry) {
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.getRawValue();
    const {address, addressPlace} = toAdminAddress(this.addressService.parseJsonAddressData(this.geometry, this.place));

    this.setupService.createLocation(this.data.landlordUuid, {
      name: v.name!,
      code: v.code!,
      address,
      addressPlace,
    })
      .pipe(
        switchMap(({uuid}) => {
          const uploads: Observable<void>[] = [];
          if (this.tileImageFile) uploads.push(this.setupService.uploadLocationTileImage(uuid, this.tileImageFile));
          if (this.bannerImageFile) uploads.push(this.setupService.uploadLocationBannerImage(uuid, this.bannerImageFile));
          return uploads.length ? forkJoin(uploads).pipe(map(() => uuid)) : of(uuid);
        }),
        this.loader.withLoader()
      )
      .subscribe({
        next: (uuid) => this.dialogRef.close(uuid),
        error: () => this.snackbar.openSnackBar('Failed to create location. Please try again.'),
      });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
